import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/layout/PageHeader';
import RecommendPrompt from '@/components/recommendation/RecommendPrompt';
import RecommendCard from '@/components/recommendation/RecommendCard';
import WineScanner from '@/components/wine/WineScanner';
import { useRecommendations } from '@/hooks/useRecommendations';
import type { Recommendation } from '../../amplify/functions/recommendations/handler';
import Button from '@/components/ui/Button';

export default function Recommend() {
  const navigate = useNavigate();
  const { recommend, loading, results, error, reset } = useRecommendations();
  const [scanMode, setScanMode] = useState(false);

  async function handlePrompt(prompt: string, context: 'CELLAR' | 'BUY') {
    await recommend({ prompt, context });
  }

  async function handleInStoreScan(_file: File) {
    setScanMode(false);
    // Scan label then get recommendation for that wine
    await recommend({ prompt: 'In-store scan — should I buy this?', context: 'BUY' });
  }

  function handleAddToCellar(rec: Recommendation) {
    navigate(`/add?wineId=${rec.wineId ?? ''}&name=${encodeURIComponent(rec.name)}`);
  }

  return (
    <div className="page page-recommend">
      <PageHeader title="Suggest" />

      {!scanMode ? (
        <>
          <RecommendPrompt onSubmit={handlePrompt} loading={loading} />

          <div className="recommend-scan-cta">
            <Button variant="ghost" onClick={() => setScanMode(true)}>
              📷 Scan a bottle in-store
            </Button>
          </div>

          {error && <p className="recommend-error">{error}</p>}

          {results.length > 0 && (
            <section className="recommend-results">
              <div className="recommend-results-header">
                <h2>Recommendations</h2>
                <button className="recommend-reset" onClick={reset}>New search</button>
              </div>
              {results.map((rec, i) => (
                <RecommendCard
                  key={i}
                  rec={rec}
                  onAddToCellar={handleAddToCellar}
                  onDismiss={() => {/* could log dislike */}}
                />
              ))}
            </section>
          )}
        </>
      ) : (
        <div className="recommend-scan-mode">
          <PageHeader title="Scan a bottle" back />
          <p className="add-instruction">Point at the label — I'll tell you if it's worth buying.</p>
          <WineScanner onCapture={handleInStoreScan} label="Scan bottle" />
          <button className="add-back-link" onClick={() => setScanMode(false)}>
            ← Back
          </button>
        </div>
      )}
    </div>
  );
}
